import type { PlanPart } from '@skoka/core';

/**
 * Покупка из чека или выписки без подходящей категории: создать новую
 * категорию, спросить человека или положить в «Прочее».
 */
export type NewCategoryMode = 'auto' | 'ask' | 'other';

/**
 * Ключ категории, которой ещё нет: её предложила модель, а создаётся она при
 * «Записать». До того она живёт только в проверке чека или выписки.
 */
export const NEW_CATEGORY = 'new:';
export const isNewCategory = (id: string | null | undefined): boolean => !!id && id.startsWith(NEW_CATEGORY);

export interface Category {
  id: string;
  kind?: 'expense' | 'income';
  archived?: boolean;
  parentId: string | null;
  name: string;
  defaultPart: PlanPart | null;
  /** Имя иконки из набора либо старый символ: см. `category-icon.tsx`. */
  icon: string;
  /** Цвет значка. Пусто — берётся по части плана. */
  color?: string;
  /** Место в списке, как человек расставил (28.10.2026). Пусто — после расставленных. */
  order?: number;
}

/**
 * Стартовый справочник (PRD §14). Девятнадцать категорий верхнего уровня,
 * Пользователь может добавлять, переименовывать и архивировать категории.
 *
 * `defaultPart: null` означает «смешанная» — часть плана решается по позиции,
 * а не по категории (инвариант И6): подгузники и дорогая игрушка обе в
 * «Ребёнок», но в разных частях плана.
 *
 * Иконки названы именами из набора, а не символами: символ зависит от шрифта
 * системы и на чужом устройстве выглядит иначе. У кого справочник уже создан,
 * значки остаются прежними — переписывать чужие данные ради единообразия
 * нельзя.
 */
const T = (id: string, name: string, part: PlanPart | null, icon: string, color: string): Category =>
  ({ id, parentId: null, name, defaultPart: part, icon, color });
const S = (id: string, parentId: string, name: string, part: PlanPart | null = null, icon = ''): Category =>
  ({ id, parentId, name, defaultPart: part, icon });

/*
  Набор для нового человека (15.10.2026): 16 категорий вместо 23, и у каждой
  своя часть плана. Прежде у 13 из них стояло «выбирать при записи», и при
  записи оставалась прошлая часть — такси незаметно уходило в нужды, и
  «Можно тратить» с «Аналитикой» делили траты неверно. «Выбирать при записи»
  осталось только у «Прочего». Ключи прежних категорий сохранены: на «groceries»,
  «other» и «other.fees» опираются разбор чеков и проценты по кредиткам.
*/
/**
 * Как выбрать часть плана — одним вопросом (28.10.2026): по названию вещи не
 * понять, нужда это или нет. Духовка взамен сломанной — нужды, телевизор
 * побольше при рабочем — свободные; решает ситуация, а не категория.
 */
export const PART_QUESTION = 'Если бы денег было впритык, эта покупка всё равно была бы нужна сейчас?';
/** Варианты части плана — со значками и цветом карточек главной (02.11.2026). */
export const PART_OPTIONS = [
  { id: 'need', title: 'Повседневные нужды', answer: 'Да, всё равно нужна', icon: 'basket-shopping-check', tone: 'var(--life)', sub: 'без этого не обойтись сейчас: продукты, лекарства, сломанное взамен' },
  { id: 'want', title: 'Свободные траты', answer: 'Нет, можно подождать', icon: 'bag-shopping-heart', tone: 'var(--self)', sub: 'можно было отложить или не покупать: кафе, обновка, техника получше' },
] as const;
/** Третий вариант у категории: часть решается при каждой трате. */
export const PART_MIXED = { id: '', title: 'Выбирать при записи', answer: 'Бывает по-разному', icon: 'scale-balanced', tone: 'var(--ink-3)', sub: 'спросим при каждой трате' } as const;

/** Что такое часть плана — одними словами везде, где её выбирают. */
export const PART_TIP = 'Повседневные нужды — без чего не обойтись: продукты, лекарства, проезд. '
  + 'Свободные траты — что можно отложить: кафе, обновки, подарки. Не ясно, куда, — спроси себя: '
  + 'если бы денег было впритык, эта покупка всё равно была бы нужна сейчас? Платёж по кредиту — не трата, а «Долг».';
/** То же для листа выбора части: вопрос там уже написан крупно. */
export const PART_TIP_SHEET = 'Повседневные нужды — без чего не обойтись: продукты, лекарства, проезд. '
  + 'Свободные траты — что можно отложить: кафе, обновки, подарки. От этого деления зависят «Можно тратить», '
  + 'план и «Аналитика». Платёж по кредиту — не трата, а «Долг».';
export const PART_TIP_CATEGORY = 'Куда пойдут траты этой категории: в повседневные нужды или в свободные траты. '
  + 'По этому делению SKOKA считает «Можно тратить», план периода и «Аналитику». «Выбирать при записи» — '
  + 'если бывает по-разному: тогда часть выбирают при каждой трате.';

export const STARTER_CATEGORIES: Category[] = [
  // Повседневные нужды
  T('groceries', 'Продукты', 'need', 'apple-whole', 'green'),
  T('housing', 'Жильё и ЖКХ', 'need', 'house', 'violet'),
  S('housing.rent', 'housing', 'Аренда и ипотека', 'need', 'key'),
  S('housing.utilities', 'housing', 'Коммунальные', 'need', 'droplet'),
  S('housing.internet', 'housing', 'Связь и интернет', 'need', 'wifi'),
  T('transport', 'Транспорт', 'need', 'bus', 'blue'),
  S('transport.public', 'transport', 'Общественный', 'need', 'train'),
  S('transport.taxi', 'transport', 'Такси', 'need', 'taxi'),
  S('transport.fuel', 'transport', 'Топливо', 'need', 'gas-pump'),
  S('transport.car', 'transport', 'Обслуживание авто', 'need', 'car-wrench'),
  T('health', 'Здоровье', 'need', 'heart-pulse', 'red'),
  S('health.doctors', 'health', 'Врачи', 'need', 'stethoscope'),
  S('health.meds', 'health', 'Лекарства', 'need', 'pills'),
  T('home', 'Дом и быт', 'need', 'couch', 'violet'),
  S('home.household', 'home', 'Бытовые товары', 'need', 'spray-can-sparkles'),
  S('home.repair', 'home', 'Ремонт', null, 'hammer'),
  S('home.furniture', 'home', 'Мебель и техника', null, 'washing-machine'),
  T('kids', 'Дети', 'need', 'baby', 'amber'),
  S('kids.school', 'kids', 'Сад и школа', 'need', 'backpack'),
  S('kids.classes', 'kids', 'Кружки', 'need', 'puzzle-piece'),
  S('kids.clothes', 'kids', 'Одежда и игрушки', 'need', 'teddy-bear'),
  T('clothes', 'Одежда и обувь', 'need', 'shirt', 'red'),
  T('pets', 'Питомцы', 'need', 'paw', 'amber'),
  T('education', 'Образование', 'need', 'graduation-cap', 'blue'),

  // Свободные траты
  T('cafe', 'Кафе и доставка', 'want', 'utensils', 'pink'),
  T('fun', 'Развлечения и хобби', 'want', 'masks-theater', 'pink'),
  T('travel', 'Путешествия', 'want', 'plane', 'blue'),
  T('gifts', 'Подарки', 'want', 'gift', 'pink'),
  T('beauty', 'Красота и уход', 'want', 'flower', 'pink'),
  T('subs', 'Подписки и сервисы', 'want', 'repeat', 'violet'),

  // Прочее: часть плана выбирается при записи. Значок — фигуры, а не три
  // точки: точки в SKOKA значат «ещё».
  T('other', 'Прочее', null, 'shapes', 'gray'),
  S('other.fees', 'other', 'Комиссии и проценты', 'need', 'percent'),
  S('other.p2p', 'other', 'Переводы людям', null, 'hand-holding-dollar'),
  S('other.uncategorized', 'other', 'Без категории', null, 'circle-question'),
];

export const topLevel = (all: Category[]): Category[] => all.filter((c) => c.parentId === null && !c.archived && c.kind !== 'income');
export const byId = (all: Category[], id: string): Category | undefined => all.find((c) => c.id === id);

/**
 * Справочник расходов для модели: имя с родителем, «Дом / Бытовые товары».
 *
 * Одно и то же для снимка и для документа: разошедшийся справочник дал бы
 * одному чеку две разные раскладки по категориям.
 */
export function modelCatalog(all: Category[]): { id: string; name: string }[] {
  const index = new Map(all.map((c) => [c.id, c]));
  const path = (c: Category, depth = 0): string => {
    const parent = c.parentId ? index.get(c.parentId) : undefined;
    return parent && depth < 3 ? `${path(parent, depth + 1)} / ${c.name}` : c.name;
  };
  return all
    .filter((c) => (c.kind ?? 'expense') === 'expense' && !c.archived)
    .map((c) => ({ id: c.id, name: path(c) }));
}
